import {
  Component,
  forwardRef,
  OnInit,
  inject,
  input,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Appointment } from '@ui/shared/model';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';

import { DateTimePipe } from '../../../../pipes/date-time.pipe';

@Component({
  selector: 'app-appointment-selection',
  templateUrl: './appointment-selection.component.html',
  styleUrls: ['./appointment-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppointmentSelectionComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslatePipe,
    DateTimePipe
  ]
})
export class AppointmentSelectionComponent
  implements OnInit, ControlValueAccessor
{
  #destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  readonly appointments = input<Appointment[]>(undefined);
  readonly applicationStatus = input<string>(undefined);
  readonly exclusiveAppointments = input<Appointment[]>(undefined);
  readonly showNotFittingOption = input(true);
  public form: FormGroup;
  public value: string;

  private onChange = (value: unknown) => value;
  private onTouch = () => null;

  get numberOfAppointments() {
    return (
      (this.appointments()?.length || 0) +
      (this.exclusiveAppointments()?.length || 0)
    );
  }

  get hasAppointments() {
    return (
      this.appointments()?.length > 0 ||
      this.exclusiveAppointments()?.length > 0
    );
  }

  get nextActiveAppointmentIdControl() {
    return this.form.get('newActiveAppointmentId');
  }

  ngOnInit() {
    this.form = this.fb.group({
      newActiveAppointmentId: ''
    });
    this.nextActiveAppointmentIdControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(v => {
        this.value = v;
        this.onChange(this.value);
        this.onTouch();
      });
  }

  public getAppointmentCanceled(appointment: Appointment) {
    return appointment.state === 'CANCELED';
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public writeValue(v: any) {
    this.value = v;
    this.nextActiveAppointmentIdControl.patchValue(v);
  }
}
